/**
 * Typed admin API used by the react-query hooks in `hooks/useApi.ts`.
 * Every call goes through the shared request core in `utils/request.ts`.
 */
import { adminRequest, adminRequestRaw, AdminApiError } from './request';
import type {
  ApiResponse,
  PaginatedResponse,
  AdminUser,
  AdminUserDetail,
  AdminOrganization,
  SystemHealth,
  PlatformAnalytics,
  AuditLogsResponse,
  CreditAdjustment,
} from '@/types';
import type { DashboardPreferences } from '@/types/dashboard';

/** Build a query string from defined, non-empty params. */
function toQuery(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  }
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

// User Management API
export const userApi = {
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<AdminUser>> =>
    adminRequest(`/users${toQuery(params)}`),

  /** One user; the backend returns the user object itself, not an envelope. */
  getUser: async (uid: string): Promise<AdminUserDetail> =>
    adminRequest(`/users/${uid}`),

  updateUserStatus: async (
    uid: string,
    status: AdminUser['status']
  ): Promise<ApiResponse> =>
    adminRequest(`/users/${uid}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  adjustCredits: async (
    uid: string,
    adjustment: CreditAdjustment
  ): Promise<ApiResponse> =>
    adminRequest(`/users/${uid}/adjust-credits`, {
      method: 'POST',
      body: JSON.stringify(adjustment),
    }),

  bulkUpdateUsers: async (
    userIds: string[],
    updates: Record<string, unknown>
  ): Promise<ApiResponse> =>
    adminRequest('/users/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ userIds, updates }),
    }),
};

// Organization Management API
export const organizationApi = {
  getOrganizations: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    plan?: string;
    type?: string;
  }): Promise<PaginatedResponse<AdminOrganization>> =>
    adminRequest(`/organizations${toQuery(params)}`),

  getOrganization: async (
    orgId: string
  ): Promise<ApiResponse<AdminOrganization>> =>
    adminRequest(`/organizations/${orgId}`),

  updateOrganization: async (
    orgId: string,
    updates: Partial<AdminOrganization>
  ): Promise<ApiResponse> =>
    adminRequest(`/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  adjustOrganizationCredits: async (
    orgId: string,
    adjustment: CreditAdjustment
  ): Promise<ApiResponse> =>
    adminRequest(`/organizations/${orgId}/adjust-credits`, {
      method: 'POST',
      body: JSON.stringify(adjustment),
    }),

  getOrganizationMembers: async (
    orgId: string
  ): Promise<ApiResponse<AdminUser[]>> =>
    adminRequest(`/organizations/${orgId}/members`),

  addOrganizationMember: async (
    orgId: string,
    email: string,
    role: string
  ): Promise<ApiResponse> =>
    adminRequest(`/organizations/${orgId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),

  removeOrganizationMember: async (
    orgId: string,
    userId: string
  ): Promise<ApiResponse> =>
    adminRequest(`/organizations/${orgId}/members/${userId}`, {
      method: 'DELETE',
    }),

  bulkUpdateOrganizations: async (
    orgIds: string[],
    updates: Record<string, unknown>
  ): Promise<{
    success: number;
    failed: number;
    errors: Array<{ id: string; error: string }>;
  }> =>
    adminRequest('/organizations/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ orgIds, updates }),
    }),
};

// System Health API
export const systemApi = {
  getSystemHealth: async (): Promise<ApiResponse<SystemHealth>> =>
    adminRequest('/system/health'),

  getAnalytics: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PlatformAnalytics>> =>
    adminRequest(`/analytics/overview${toQuery(params)}`),
};

// Audit Logs API
export const auditApi = {
  getAuditLogs: async (params?: {
    page?: number;
    limit?: number;
    action?: string;
    targetUserId?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AuditLogsResponse> =>
    adminRequest(`/audit-logs${toQuery(params)}`),

  exportAuditLogs: async (params?: {
    format?: 'csv' | 'json';
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> => {
    const response = await adminRequestRaw(
      `/export/audit-logs${toQuery(params)}`
    );
    return response.blob();
  },
};

// Dashboard Preferences API (SPRITE-666)
// Per-admin layout/settings persisted server-side; the hook keeps localStorage as an offline cache.
export const dashboardPreferencesApi = {
  get: async (): Promise<{
    preferences: DashboardPreferences | null;
    updatedAt: string | null;
  }> => adminRequest('/dashboard-preferences'),

  save: async (preferences: DashboardPreferences): Promise<ApiResponse> =>
    adminRequest('/dashboard-preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    }),
};

/** Kept for existing imports; the one error class lives in `utils/request.ts`. */
export { AdminApiError as ApiError };
